import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import { useNavigate } from 'react-router-dom';
import { isMobile, isTablet } from 'react-device-detect';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';

export interface RowConfig<T extends object> {
    display_row: (data: T) => JSX.Element;
}

export interface MobileDataCardProps<T extends object> {
    id: string;
    data: T;
    rowConfigs: RowConfig<T>[];
    onCardClick?: (d: T) => void;
    cardIcon?: JSX.Element;
    onIconClick?: (d: T) => void;
    iconBoxStyle?: React.CSSProperties;
    footer?: {
        content: JSX.Element,
        background: string,
    },
    selectedItems: Map<string, T>,
    toggleIdSelection: (d: T) => void,
    selectionEnabled?: boolean,
}

export default function MobileDataCard<T extends object>({ data, selectionEnabled, toggleIdSelection, selectedItems, onCardClick, id, cardIcon, rowConfigs, iconBoxStyle, footer, onIconClick}: MobileDataCardProps<T>) {
    const [selected, setSelected] = React.useState(false);
    const navigate = useNavigate();

    const handleCardClick = () => onCardClick && onCardClick(data);

    const handleIconClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Stop event from bubbling up
        if (onIconClick) onIconClick(data);
    };

    const { color } = React.useContext(OrgSettingsContext);

    return (
        <Box sx={{ minWidth: 275, my: 2, px: 0, position: 'relative', }} >
            <Card
                elevation={5}
                onClick={handleCardClick}
                sx={{ borderRadius: '15px', }}
            >
                <Box
                    sx={{
                        borderLeft: selected ? `4px solid ${color}` : '4px solid transparent',
                        padding: '10px',

                        '@media (max-width: 600px)': {
                            padding: '5px',
                        },
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexWrap: "nowrap",
                            alignItems: "center"
                        }}
                    >
                        {
                            selectionEnabled && <Checkbox
                                checked={selectedItems.has(id)}
                                onChange={(e) => {
                                    toggleIdSelection(data);
                                }}
                                onClick={(event) => event.stopPropagation()}
                            />

                        }
                        {cardIcon &&
                            <Box onClick={handleIconClick}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'absolute',
                                    top: 20,
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    borderRadius: '50%',
                                    ...iconBoxStyle,
                                }}
                            >
                                {cardIcon}
                            </Box>
                        }
                    </div>
                    <CardContent
                        sx={{
                            padding: (isMobile && !isTablet) ? '8px 4px' : '10px 0',
                            ...((isMobile && !isTablet) && {
                                '&:last-child': {
                                    paddingBottom: '5px',
                                },
                            })
                        }}
                    >
                        {rowConfigs.map((config, index) => (
                            <Typography variant="body2" key={index} sx={{ fontSize: { xs: '0.8rem', md: '1rem' }, mb: 1 }}>
                                {config.display_row(data)}
                            </Typography>
                        ))}
                    </CardContent>
                </Box>

                {footer && <Box sx={{ backgroundColor: footer.background, padding: 0, textAlign: 'center', borderRadius: '0 0 15px 15px', overflow: 'hidden', margin: 0 }}>{footer.content}</Box>}
            </Card >

        </ Box >
    );
}